import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Component, ContentChild, EventEmitter, forwardRef, Input, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import {
    FilterSelectFilterTemplateDirective,
    FilterSelectFooterTemplateDirective,
    FilterSelectItemTemplateDirective,
    FilterSelectSelectedItemTemplateDirective,
} from './filter-select-templates.directive';

@Component({
    selector: 'app-filter-select',
    templateUrl: './filter-select.component.html',
    styleUrls: ['./filter-select.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FilterSelectComponent),
            multi: true,
        },
    ],
    standalone: false,
})
export class FilterSelectComponent implements ControlValueAccessor {
    @Input() id?: string;
    @Input() placeholder?: string;

    @Input() options: any[] = [];
    /** PrimeNG default is "label" */
    @Input() optionLabel = 'label';
    @Input() optionValue?: string;
    @Input() filter = false;
    @Input() filterBy?: string;
    @Input() showClear = false;
    @Input() virtualScroll = false;
    @Input() virtualScrollItemSize?: number;
    @Input() disabled = false;

    @Input() style?: any;
    @Input() styleClass?: string;
    @Input() panelStyleClass?: string;
    @Input() appendTo?: any;
    @Input() baseZIndex?: number;

    /** PrimeNG passthroughs commonly used in templates */
    @Input() autoDisplayFirst?: boolean;
    @Input() emptyMessage?: string;
    @Input() resetFilterOnHide?: boolean;

    /** Maintain API parity with `<p-select (onChange)=...>` */
    @Output() onChange = new EventEmitter<any>();
    @Output() onShow = new EventEmitter<any>();
    @Output() onHide = new EventEmitter<any>();
    @Output() onFocus = new EventEmitter<any>();
    @Output() onBlur = new EventEmitter<any>();

    @ViewChild(MatSelect) matSelect?: MatSelect;
    @ViewChild(CdkVirtualScrollViewport) viewport?: CdkVirtualScrollViewport;

    @ContentChild(FilterSelectItemTemplateDirective) itemTemplate?: FilterSelectItemTemplateDirective;
    @ContentChild(FilterSelectSelectedItemTemplateDirective) selectedItemTemplate?: FilterSelectSelectedItemTemplateDirective;
    @ContentChild(FilterSelectFilterTemplateDirective) filterTemplate?: FilterSelectFilterTemplateDirective;
    @ContentChild(FilterSelectFooterTemplateDirective) footerTemplate?: FilterSelectFooterTemplateDirective;

    value: any = undefined;
    filterText = '';
    isOpen = false;

    get panelClasses(): string[] {
        const base = ['app-filter-select__panel'];
        if (!this.panelStyleClass) {
            return base;
        }
        // Support space-delimited class strings
        const extras = String(this.panelStyleClass)
            .split(' ')
            .map((c) => c.trim())
            .filter(Boolean);
        return [...base, ...extras];
    }

    private onChangeFn: (value: any) => void = () => {};
    private onTouchedFn: () => void = () => {};

    writeValue(value: any): void {
        this.value = value;
    }

    registerOnChange(fn: (value: any) => void): void {
        this.onChangeFn = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouchedFn = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    show(): void {
        this.matSelect?.open();
    }

    hide(): void {
        this.matSelect?.close();
    }

    clear(evt?: Event): void {
        if (evt) {
            evt.preventDefault();
            evt.stopPropagation();
        }
        this.onMatSelectionChange({ value: undefined } as MatSelectChange);
    }

    onMatSelectionChange(event: MatSelectChange): void {
        const next = event?.value;
        this.value = next;
        this.onChangeFn(next);
        this.onChange.emit({ originalEvent: event, value: next });
    }

    handleOpenedChange(isOpen: boolean): void {
        this.isOpen = isOpen;
        if (isOpen) {
            this.onShow.emit({ open: true });
            // ensure viewport recalculates after panel opens, and fix overlay sizing
            setTimeout(() => {
                this.viewport?.checkViewportSize();
                this.syncOverlayWidth();
            }, 0);
        } else {
            if (this.resetFilterOnHide) {
                this.filterText = '';
            }
            this.onHide.emit({ open: false });
        }
    }

    handleFocus(evt: FocusEvent): void {
        this.onFocus.emit(evt);
    }

    handleBlur(evt: any): void {
        this.onTouchedFn();
        this.onBlur.emit(evt);
    }

    get displayedOptions(): any[] {
        const opts = this.options ?? [];
        // If caller provides a custom filter template, they likely manage `options` themselves (remote/custom filter UX).
        if (!this.filter || this.filterTemplate?.templateRef) {
            return opts;
        }
        const q = (this.filterText || '').toLowerCase().trim();
        if (!q) {
            return opts;
        }
        const fields = (this.filterBy || this.optionLabel || 'label')
            .split(',')
            .map((f) => f.trim())
            .filter(Boolean);
        return opts.filter((o) => {
            const haystacks = fields.map((f) => String(this.getField(o, f) ?? '').toLowerCase());
            return haystacks.some((h) => h.includes(q));
        });
    }

    get selectedOption(): any {
        if (!this.optionValue) {
            return this.value;
        }
        return (this.options ?? []).find((o) => this.getOptionValue(o) === this.value);
    }

    get isValueEmpty(): boolean {
        return this.value === undefined || this.value === null || this.value === '';
    }

    get itemSizePx(): number {
        return this.virtualScrollItemSize ?? 48;
    }

    get viewportHeightPx(): number {
        if (!this.virtualScroll) {
            return 0;
        }
        const maxVisible = 8;
        const visible = Math.min(this.displayedOptions.length || 0, maxVisible);
        const rows = Math.max(visible, 1);
        return rows * this.itemSizePx;
    }

    getOptionLabel(option: any): any {
        if (option === undefined || option === null) {
            return '';
        }
        if (typeof option === 'string' || typeof option === 'number') {
            return option;
        }
        return option?.[this.optionLabel];
    }

    getOptionValue(option: any): any {
        if (option === undefined || option === null) {
            return undefined;
        }
        // Match PrimeNG behavior: if `optionValue` not provided, value is the option object itself.
        if (!this.optionValue) {
            return option;
        }
        if (typeof option === 'string' || typeof option === 'number') {
            return option;
        }
        return option?.[this.optionValue];
    }

    private getField(option: any, field: string): any {
        if (option === undefined || option === null) {
            return '';
        }
        if (typeof option === 'string' || typeof option === 'number') {
            return option;
        }
        return option?.[field];
    }

    /**
     * Material sets the overlay pane width to the trigger width.
     * To allow the panel to grow for long option labels while still being >= trigger width,
     * we adjust the overlay pane inline styles when opened.
     */
    private syncOverlayWidth(): void {
        const matSelectAny: any = this.matSelect as any;
        const panelEl: HTMLElement | undefined = matSelectAny?.panel?.nativeElement;
        const triggerEl: HTMLElement | undefined = matSelectAny?._elementRef?.nativeElement;
        if (!panelEl || !triggerEl) {
            return;
        }

        const pane = panelEl.closest('.cdk-overlay-pane') as HTMLElement | null;
        if (!pane) {
            return;
        }

        const triggerWidth = Math.ceil(triggerEl.getBoundingClientRect().width);
        pane.style.minWidth = `${triggerWidth}px`;
        pane.style.width = 'auto';
        // Let the panel itself decide how wide it needs to be.
        panelEl.style.width = 'max-content';
        panelEl.style.maxWidth = '90vw';
    }
}

